import { Form, Formik, type FormikHelpers, useField, useFormikContext } from "formik";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { RichTextEditor } from "@/components/common/rich-text-editor";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  createPractitioner,
  DESIGNATION_OPTIONS,
  type Practitioner,
  type PractitionerDesignation,
  type PractitionerPayload,
  updatePractitioner,
} from "@/features/practitioners/api";
import { US_STATE_OPTIONS } from "@/features/practitioners/us-states";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SelectField } from "@/features/settings/components/select-field";
import { applyApiErrors } from "@/lib/form-errors";
import { textToHtml } from "@/lib/rich-text";

const ABOUT_MAX = 3000;
// Radix Select forbids an empty value, so "no state" needs a sentinel.
const STATE_NONE = "__none__";
const STATE_OPTIONS = [{ value: STATE_NONE, label: "— None —" }, ...US_STATE_OPTIONS];

// Attorney (a) / CPA (b) are licensed by a state; EA (c) is always the IRS.
function isStateLicensed(d: PractitionerDesignation) {
  return d === "a" || d === "b";
}

const A2A_RE = /^([A-Z0-9]+-\d+)?$/i;

const schema = Yup.object({
  businessCaf: Yup.boolean(),
  firstName: Yup.string()
    .trim()
    .max(60)
    .when("businessCaf", {
      is: false,
      then: (s) => s.required("First name is required"),
    }),
  lastName: Yup.string()
    .trim()
    .max(60)
    .required("This field is required"),
  email: Yup.string().email("Enter a valid email").max(254),
  phone: Yup.string().max(32),
  fax: Yup.string().max(32),
  cell: Yup.string().max(32),
  addressLine1: Yup.string().max(120),
  addressLine2: Yup.string().max(120),
  city: Yup.string().max(60),
  zipCode: Yup.string().matches(/^(\d{5}(-\d{4})?)?$/, "ZIP must be 5 digits or ZIP+4"),
  firmName: Yup.string().max(120),
  firmEin: Yup.string().matches(/^(\d{2}-?\d{7})?$/, "EIN must be 9 digits (XX-XXXXXXX)"),
  designation: Yup.string().required(),
  ptin: Yup.string().matches(/^(P\d{8})?$/, "PTIN must be P followed by 8 digits"),
  cafNumber: Yup.string().matches(/^\d{0,9}$/, "CAF must be up to 9 digits"),
  efin: Yup.string().matches(/^(\d{6})?$/, "EFIN must be 6 digits"),
  ctec: Yup.string().max(20),
  nyptrin: Yup.string().max(20),
  licensing: Yup.string().max(40),
  licenseNumber: Yup.string().max(40),
  esrvUserId: Yup.string().matches(A2A_RE, "Invalid A2A user ID (e.g. ABC123-456)"),
  aboutText: Yup.string().max(ABOUT_MAX, `Max ${ABOUT_MAX} characters`),
});

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  fax: string;
  cell: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  firmName: string;
  firmEin: string;
  designation: PractitionerDesignation;
  ptin: string;
  cafNumber: string;
  efin: string;
  ctec: string;
  nyptrin: string;
  licensing: string;
  licenseNumber: string;
  businessCaf: boolean;
  esrvUserId: string;
  aboutText: string;
}

/** EA license numbers print as zero-padded 8 digits + `-EA` (legacy rule). */
function normalizeEaLicense(designation: PractitionerDesignation, license: string) {
  if (designation !== "c") return license;
  const digits = license.replace(/\D/g, "");
  if (!digits) return license;
  return `${digits.slice(0, 8).padStart(8, "0")}-EA`;
}

/** Consistent section eyebrow for the dialog's grouped fields. */
function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase">
      {children}
    </h3>
  );
}

/** Name inputs swap to a single Business Name when Business-CAF is on. */
function NameFields() {
  const { values } = useFormikContext<FormValues>();
  if (values.businessCaf) {
    return <FormField name="lastName" label="Business Name" />;
  }
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <FormField name="firstName" label="First Name" />
      <FormField name="lastName" label="Last Name" />
    </div>
  );
}

/**
 * Licensing input adapts to the designation (state dropdown / IRS / free).
 * EA's "IRS" jurisdiction is shown read-only and applied at submit time (see
 * `handleSubmit`) rather than written into Formik state via an effect.
 */
function LicensingFields() {
  const { values } = useFormikContext<FormValues>();
  const isEA = values.designation === "c";

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {isStateLicensed(values.designation) ? (
        <SelectField name="licensing" label="Licensing (State)" options={STATE_OPTIONS} />
      ) : isEA ? (
        <div className="grid gap-2">
          <Label htmlFor="licensing">Licensing / Jurisdiction</Label>
          <Input id="licensing" value="IRS" readOnly disabled />
        </div>
      ) : (
        <FormField name="licensing" label="Licensing / Jurisdiction" />
      )}
      <FormField name="licenseNumber" label="License No." />
    </div>
  );
}

function AboutField() {
  const [field, meta, helpers] = useField<string>("aboutText");
  const [preview, setPreview] = useState(false);
  const hasError = meta.touched && !!meta.error;
  // Counts the stored HTML (what the server's max-length checks too).
  const length = field.value?.length ?? 0;
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="aboutText">About</Label>
        <button
          type="button"
          className="text-muted-foreground hover:text-primary text-xs font-medium"
          onClick={() => setPreview((p) => !p)}
        >
          {preview ? "Edit" : "Preview"}
        </button>
      </div>
      {preview ? (
        <div className="border-input min-h-24 rounded-md border p-3">
          {field.value ? (
            <SanitizedHtml html={field.value} />
          ) : (
            <span className="text-muted-foreground text-sm">Nothing to preview.</span>
          )}
        </div>
      ) : (
        <RichTextEditor
          id="aboutText"
          ariaLabel="About"
          ariaInvalid={hasError}
          minHeight={110}
          value={field.value}
          onChange={(html) => {
            helpers.setValue(html);
            if (!meta.touched) helpers.setTouched(true, false);
          }}
        />
      )}
      <div className="flex items-center justify-between">
        {hasError ? (
          <p className="text-destructive text-sm">{meta.error}</p>
        ) : (
          <span />
        )}
        <span className="text-muted-foreground text-xs">
          {length}/{ABOUT_MAX}
        </span>
      </div>
    </div>
  );
}

interface PractitionerDialogProps {
  /** Existing practitioner to edit; omit to create a new one. */
  practitioner?: Practitioner;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (p: Practitioner) => void;
}

export function PractitionerDialog({
  practitioner,
  open,
  onOpenChange,
  onSaved,
}: PractitionerDialogProps) {
  const isEdit = !!practitioner;

  async function handleSubmit(values: FormValues, { setErrors }: FormikHelpers<FormValues>) {
    const payload: PractitionerPayload = {
      ...values,
      firstName: values.businessCaf ? "" : values.firstName,
      state: values.state === STATE_NONE ? "" : values.state,
      licensing:
        values.designation === "c"
          ? "IRS" // EA is always licensed by the IRS
          : isStateLicensed(values.designation) && values.licensing === STATE_NONE
            ? ""
            : values.licensing,
      licenseNumber: normalizeEaLicense(values.designation, values.licenseNumber),
      // Write-only credential: send only when entered so a blank field on edit
      // keeps the stored EFIN.
      efin: values.efin || undefined,
    };
    try {
      const saved = isEdit
        ? await updatePractitioner(practitioner.id, payload)
        : await createPractitioner(payload);
      onSaved(saved);
      onOpenChange(false);
      toast.success(isEdit ? "Practitioner updated" : "Practitioner added");
    } catch (err) {
      toast.error(
        applyApiErrors(err, { setErrors }, { fallback: "Couldn't save the practitioner." }),
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit practitioner" : "Add practitioner"}</DialogTitle>
          <DialogDescription>
            Practitioners represent the firm on IRS forms and e-Services.
          </DialogDescription>
        </DialogHeader>
        <Formik<FormValues>
          initialValues={{
            firstName: practitioner?.firstName ?? "",
            lastName: practitioner?.lastName ?? "",
            email: practitioner?.email ?? "",
            phone: practitioner?.phone ?? "",
            fax: practitioner?.fax ?? "",
            cell: practitioner?.cell ?? "",
            addressLine1: practitioner?.addressLine1 ?? "",
            addressLine2: practitioner?.addressLine2 ?? "",
            city: practitioner?.city ?? "",
            state: practitioner?.state || STATE_NONE,
            zipCode: practitioner?.zipCode ?? "",
            firmName: practitioner?.firmName ?? "",
            firmEin: practitioner?.firmEin ?? "",
            designation: practitioner?.designation ?? "h",
            ptin: practitioner?.ptin ?? "",
            cafNumber: practitioner?.cafNumber ?? "",
            efin: "",
            ctec: practitioner?.ctec ?? "",
            nyptrin: practitioner?.nyptrin ?? "",
            licensing: practitioner?.licensing ?? "",
            licenseNumber: practitioner?.licenseNumber ?? "",
            businessCaf: practitioner?.businessCaf ?? false,
            esrvUserId: practitioner?.esrvUserId ?? "",
            // Legacy about text is plain — upgrade to HTML for the editor.
            aboutText: textToHtml(practitioner?.aboutText),
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form className="grid gap-6">
              <CheckboxField
                name="businessCaf"
                label="Business CAF practitioner (can only be used on Form 8821)"
                hint="Uses a single business name instead of a first/last name."
              />

              <section className="grid gap-5">
                <SectionHeading>Personal details</SectionHeading>
                <NameFields />
                <div className="grid gap-5 sm:grid-cols-2">
                  <FormField name="email" label="Email" type="email" />
                  <SelectField
                    name="designation"
                    label="Designation"
                    options={DESIGNATION_OPTIONS}
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-3">
                  <FormField name="phone" label="Phone" />
                  <FormField name="cell" label="Cell" />
                  <FormField name="fax" label="Fax" />
                </div>
              </section>

              <section className="grid gap-5 border-t pt-6">
                <SectionHeading>Firm &amp; address</SectionHeading>
                <FormField name="firmName" label="Firm Name" />
                <FormField name="addressLine1" label="Address 1" />
                <FormField name="addressLine2" label="Address 2" />
                <div className="grid gap-5 sm:grid-cols-3">
                  <FormField name="city" label="City" />
                  <SelectField name="state" label="State" options={STATE_OPTIONS} />
                  <FormField name="zipCode" label="ZIP Code" placeholder="94107" />
                </div>
              </section>

              <section className="grid gap-5 border-t pt-6">
                <SectionHeading>IRS credentials</SectionHeading>
                <div className="grid gap-5 sm:grid-cols-2">
                  <FormField name="firmEin" label="Firm EIN" placeholder="12-3456789" />
                  <FormField name="ptin" label="PTIN" placeholder="P12345678" />
                </div>
                <div className="grid gap-5 sm:grid-cols-3">
                  <FormField name="cafNumber" label="CAF No." />
                  <FormField
                    name="efin"
                    label="EFIN"
                    placeholder={practitioner?.hasEfin ? "•••••• (on file)" : "123456"}
                  />
                  <FormField name="ctec" label="CTEC" />
                  <FormField name="nyptrin" label="NYTPRIN" />
                </div>
                <LicensingFields />
              </section>

              <section className="grid gap-5 border-t pt-6">
                <SectionHeading>Automatic connection to TDS (A2A)</SectionHeading>
                <FormField
                  name="esrvUserId"
                  label="A2A Full User ID"
                  placeholder="ABC123-456"
                  className="uppercase"
                />
              </section>

              {!values.businessCaf && <AboutField />}

              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Add Practitioner"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
